import React, { useEffect, useRef, useState } from 'react';

import { VISIT_SEARCH_COPY, VISIT_SEARCH_DEBOUNCE_MS } from '../../utils/visitSearch';

/**
 * VisitSearchInput — the ONE patient-search box on the visits page (D44 / S19).
 *
 * Both surfaces that page serves — the admin "Manage Visits" module and the
 * doctor's own visits — render this component, so there is no second
 * implementation to drift.
 *
 * It owns exactly two things: the text the doctor sees, and the debounce. The
 * page owns the filtering, so the box cannot filter one list while the page
 * counts another.
 *
 * `Esc` and the ✕ clear it and leave the caret in the box — clearing is how the
 * doctor gets the full list back, so it must never cost a second click.
 */
export default function VisitSearchInput({ value, onChange, isDark = false, style }) {
  const [text, setText] = useState(value || '');
  const inputRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Debounced: typing filters the loaded page, it never fetches, and it never
  // re-filters on every keystroke of a long name.
  useEffect(() => {
    if (text === value) return undefined;
    const t = setTimeout(() => onChangeRef.current(text), VISIT_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [text, value]);

  // The page can clear the term (a filter change, a reset): follow it.
  useEffect(() => {
    if (value === '') setText((prev) => (prev === '' ? prev : ''));
  }, [value]);

  const clear = () => {
    setText('');
    onChangeRef.current('');
    inputRef.current?.focus();
  };

  const ink = isDark ? '#e8e9ed' : '#374151';
  // AC9 — bolder so it reads as a search box at a glance: a 1.5px slate-400+
  // border (CLAUDE.md day-mode rule: light-mode borders at slate-400 weight
  // or stronger), darkened further on focus with a soft outer ring.
  const border = isDark ? 'rgba(255,255,255,0.30)' : '#94a3b8';
  const placeholderInk = isDark ? '#94a3b8' : '#64748b';

  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', ...style }}>
      {/* Pseudo-classes/elements can't go in an inline `style` — scoped to
          this one input via its stable data-testid (the ONE instance on the
          page). */}
      <style>{`
        input[data-testid="visit-search"]:focus {
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37,99,235,0.18);
        }
        input[data-testid="visit-search"]::placeholder {
          color: ${placeholderInk};
        }
      `}</style>
      <span
        aria-hidden="true"
        data-testid="visit-search-icon"
        style={{
          position: 'absolute', left: '0.625rem', top: '50%', transform: 'translateY(-50%)',
          fontSize: '0.8125rem', lineHeight: 1, color: placeholderInk, pointerEvents: 'none',
        }}
      >🔍</span>
      <input
        ref={inputRef}
        type="search"
        data-testid="visit-search"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { e.preventDefault(); clear(); }
        }}
        placeholder={VISIT_SEARCH_COPY.placeholder}
        aria-label={VISIT_SEARCH_COPY.aria}
        style={{
          padding: '0.5rem 2rem 0.5rem 1.75rem',
          borderRadius: '0.5rem',
          border: `1.5px solid ${border}`,
          background: isDark ? '#1e293b' : '#ffffff',
          color: ink,
          fontSize: '0.875rem',
          fontFamily: 'system-ui, sans-serif',
          width: '100%',
          boxSizing: 'border-box',
          outline: 'none',
        }}
      />
      {text !== '' && (
        <button
          type="button"
          data-testid="visit-search-clear"
          onClick={clear}
          aria-label={VISIT_SEARCH_COPY.clearAria}
          title={VISIT_SEARCH_COPY.clearAria}
          style={{
            position: 'absolute', right: '0.35rem',
            width: '1.25rem', height: '1.25rem', lineHeight: 1,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: '50%', border: 'none', cursor: 'pointer',
            background: 'transparent', color: isDark ? '#94a3b8' : '#64748b',
            fontSize: '0.875rem', fontWeight: 700, padding: 0,
          }}
        >✕</button>
      )}
    </span>
  );
}
